import { mapValues } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import { useAuthUser } from 'auth/hooks';
import { ContactGroupType, ContactStatus } from 'contacts/enums';
import { contactService } from 'contacts/services';
import { IContact, IContactGroup } from 'contacts/types';
import { AgGridFilterOption, TableLayoutType } from 'core/enums';
import { AgGridServerTableProvider } from 'core/states';
import { AgGridGetRowsParams } from 'core/types';
import { removeNullValuesNestedObject } from 'core/utils';

import { contactColumnDef } from './columns';
import { ContactListTable } from './ContactListTable';
import { contactRowClassRules } from './table';

interface IProps {
  groupType: ContactGroupType;
}

export const ContactList = ({ groupType }: IProps) => {
  const [selectedContactGroup, setSelectedContactGroup] = useState<IContactGroup | null>(null);
  const [selectedContact, setSelectedContact] = useState<IContact | null>(null);

  const openContactGroupDetails = useCallback(
    (data: IContact) =>
      setSelectedContactGroup({
        ...data?.contactGroup,
      } as IContactGroup),
    []
  );

  const openContactDetails = useCallback((data: IContact) => {
    setSelectedContact(data);
  }, []);

  const { user } = useAuthUser();

  const columnDefs = useMemo(
    () => contactColumnDef({ openContactGroupDetails, openContactDetails }),
    [openContactGroupDetails, openContactDetails]
  );

  const initialState = useMemo(() => {
    return {
      filter: {
        filterModel: {
          deleted: {
            filterType: 'set',
            values: [ContactStatus.ACTIVE],
          },
          ownerName: {
            filterType: 'text',
            type: AgGridFilterOption.CONTAINS,
            filter: `${user?.firstName} ${user?.lastName}`,
          },
        },
      },
    };
  }, [user?.firstName, user?.lastName]);

  const mapFilters = useCallback((filters) => {
    let mappedFilters = filters;

    mappedFilters = mapValues(mappedFilters, (value, key) => {
      switch (key) {
        case 'deleted':
          if (value.includes(ContactStatus.DELETED) && !value.includes(ContactStatus.ACTIVE)) {
            return '1';
          }

          if (value.includes(ContactStatus.ACTIVE) && !value.includes(ContactStatus.DELETED)) {
            return '0';
          }

          return null;
        default:
          return value;
      }
    });

    return removeNullValuesNestedObject(mappedFilters);
  }, []);

  const getRows = useCallback(
    ({ filters, ...rest }: AgGridGetRowsParams) => {
      return contactService.getContacts({
        filters: {
          groupType,
          ...mapFilters(filters),
        },
        includeContactGroups: 0,
        ...rest,
      });
    },
    [groupType, mapFilters]
  );

  const getLayoutType = useCallback(() => {
    switch (groupType) {
      case ContactGroupType.VENDOR:
        return TableLayoutType.VENDOR_CONTACT;
      case ContactGroupType.CUSTOMER:
      default:
        return TableLayoutType.CUSTOMER_CONTACT;
    }
  }, [groupType]);

  return (
    <AgGridServerTableProvider
      key={user?.id}
      tableId={'contactList'}
      entityType="Contact"
      columnDefs={columnDefs}
      initialState={initialState}
      getRows={getRows}
      layoutType={getLayoutType()}
      rowClassRules={contactRowClassRules}
      hasRowSelection
      hasLayoutActions
      hasManageFilters
    >
      <ContactListTable
        selectedContactGroup={selectedContactGroup}
        setSelectedContactGroup={setSelectedContactGroup}
        selectedContact={selectedContact}
        setSelectedContact={setSelectedContact}
        groupType={groupType}
      />
    </AgGridServerTableProvider>
  );
};
